import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import {
  Plus,
  Search,
  Grid3X3,
  List,
  Package,
  Edit,
  Trash2,
  Eye,
  DollarSign,
  ShoppingCart,
  Settings,
  ChevronDown,
  ChevronRight,
  X,
  SlidersHorizontal,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Calendar,
  Receipt
} from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { Input } from '@/components/ui/Input'
import { materialsApi, apiClient } from '@/services/api'
import { useCurrentTemplate } from '@/hooks/useCurrentTemplate'

interface Material {
  id: string
  name: string
  sku: string
  category: string
  categoryId: string
  categoryEntity?: {
    id: string
    name: string
  }
  brand: string
  brandId: string
  brandEntity?: {
    id: string
    name: string
    website?: string
  }
  colorId: string
  colorEntity?: {
    id: string
    name: string
    hexCode?: string
  }
  styleId: string
  styleEntity?: {
    id: string
    name: string
    category?: string
  }
  purityId: string
  purityEntity?: {
    id: string
    name: string
    category?: string
  }
  templateCategory?: {
    id: string
    name: string
  }
  templateUnitType?: {
    id: string
    name: string
    abbreviation: string
  }
  currentStock: number
  reorderPoint: number
  maxStock: number
  imageUrl?: string
  imageUrls?: string[]
  specifications: {
    color?: string
    gauge?: string
    material?: string
    finish?: string
    diameter?: string
    length?: string
    width?: string
    thickness?: string
    purity?: string
    grade?: string
    clarity?: string
    cut?: string
    shape?: string
    style?: string
    coating?: string
    hardness?: string
  }
  averageCost: number
  lastCostPerUnit: number
}

interface Purchase {
  id: string
  quantity: number
  unitCost: number
  totalCost: number
  supplier?: string
  purchaseDate: string
  invoiceNumber?: string
  notes?: string
  packageQuantity?: number
  itemsPerPackage?: number
  totalItems?: number
  costPerPackage?: number
  createdAt: string
}

interface MaterialCategory {
  id: string
  name: string
}

interface MaterialBrand {
  id: string
  name: string
}

interface MaterialColor {
  id: string
  name: string
  hexCode?: string
}

interface MaterialStyle {
  id: string
  name: string
  category?: string
}

interface MaterialPurity {
  id: string
  name: string
  category?: string
}

interface SortConfig {
  field: string
  direction: 'asc' | 'desc'
}

interface FilterConfig {
  categories: string[]
  brands: string[]
  colors: string[]
  styles: string[]
  purities: string[]
  specifications: {
    finishes: string[]
    diameters: string[]
    hardness: string[]
    materials: string[]
    gauges: string[]
  }
  stockStatus: string[]
  priceRange: {
    min: number | null
    max: number | null
  }
}

export const MaterialsListPage: React.FC = () => {
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('list')
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('all')
  const [selectedMaterials, setSelectedMaterials] = useState<Set<string>>(new Set())
  const [showSellModal, setShowSellModal] = useState<string | null>(null)
  const [showBulkSale, setShowBulkSale] = useState(false)
  const [sellQuantity, setSellQuantity] = useState(1)
  const [sellPrice, setSellPrice] = useState('')
  const [materials, setMaterials] = useState<Material[]>([])
  const [categories, setCategories] = useState<MaterialCategory[]>([])
  const [brands, setBrands] = useState<MaterialBrand[]>([])
  const [colors, setColors] = useState<MaterialColor[]>([])
  const [styles, setStyles] = useState<MaterialStyle[]>([])
  const [purities, setPurities] = useState<MaterialPurity[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false)
  
  // Purchase history state
  const [purchaseHistory, setPurchaseHistory] = useState<Record<string, Purchase[]>>({})
  const [showPurchaseHistory, setShowPurchaseHistory] = useState<Record<string, boolean>>({})
  const [loadingPurchases, setLoadingPurchases] = useState<Record<string, boolean>>({})
  
  // Advanced filtering and sorting state
  const [sortConfig, setSortConfig] = useState<SortConfig>({ field: 'name', direction: 'asc' })
  const [filters, setFilters] = useState<FilterConfig>({
    categories: [],
    brands: [],
    colors: [],
    styles: [],
    purities: [],
    specifications: {
      finishes: [],
      diameters: [],
      hardness: [],
      materials: [],
      gauges: []
    },
    stockStatus: [],
    priceRange: { min: null, max: null }
  })

  // Get current template for template-aware functionality
  const { template: currentTemplate, loading: templateLoading } = useCurrentTemplate()

  useEffect(() => {
    fetchMaterials()
    fetchCategories()
    fetchBrands()
    fetchColors()
    fetchStyles()
    fetchPurities()
  }, [])

  // Refetch data when template changes
  useEffect(() => {
    if (currentTemplate && !templateLoading) {
      fetchBrands()
      fetchColors()
      fetchStyles()
      fetchPurities()
    }
  }, [currentTemplate?.id])

  // Refresh materials when the page becomes visible (e.g., after navigation back from purchase page)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        fetchMaterials()
      }
    }

    const handleFocus = () => {
      fetchMaterials()
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('focus', handleFocus)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('focus', handleFocus)
    }
  }, [])

  // Advanced filtering and sorting logic
  const getUniqueSpecificationValues = (field: keyof Material['specifications']) => {
    const values = new Set<string>()
    materials.forEach(material => {
      const value = material.specifications?.[field]
      if (value && typeof value === 'string') {
        values.add(value)
      }
    })
    return Array.from(values).sort()
  }

  const applyAdvancedFilters = (material: Material): boolean => {
    // Basic search
    const brandName = material.brandEntity?.name || material.brand || ''
    const colorName = material.colorEntity?.name || material.specifications?.color || ''
    const styleName = material.styleEntity?.name || material.specifications?.style || ''
    const purityName = material.purityEntity?.name || material.specifications?.purity || ''
    
    const matchesSearch = searchTerm === '' || [
      material.name,
      material.sku,
      brandName,
      colorName,
      styleName,
      purityName,
      material.specifications?.material,
      material.specifications?.finish,
      material.specifications?.gauge
    ].some(field => field?.toLowerCase().includes(searchTerm.toLowerCase()))

    // Category filter
    const matchesCategory = selectedCategory === 'all' ||
                           material.categoryId === selectedCategory ||
                           material.category === selectedCategory

    // Advanced filters
    const matchesAdvancedFilters = !showAdvancedFilters || (
      // Categories
      (filters.categories.length === 0 || filters.categories.includes(material.categoryId || material.category)) &&
      
      // Brands
      (filters.brands.length === 0 || filters.brands.includes(material.brandId || material.brand)) &&
      
      // Colors
      (filters.colors.length === 0 || filters.colors.includes(material.colorId || material.specifications?.color || '')) &&
      
      // Styles
      (filters.styles.length === 0 || filters.styles.includes(material.styleId || material.specifications?.style || '')) &&
      
      // Purities
      (filters.purities.length === 0 || filters.purities.includes(material.purityId || material.specifications?.purity || '')) &&
      
      // Specifications
      (filters.specifications.finishes.length === 0 || filters.specifications.finishes.includes(material.specifications?.finish || '')) &&
      (filters.specifications.diameters.length === 0 || filters.specifications.diameters.includes(material.specifications?.diameter || '')) &&
      (filters.specifications.hardness.length === 0 || filters.specifications.hardness.includes(material.specifications?.hardness || '')) &&
      (filters.specifications.materials.length === 0 || filters.specifications.materials.includes(material.specifications?.material || '')) &&
      (filters.specifications.gauges.length === 0 || filters.specifications.gauges.includes(material.specifications?.gauge || '')) &&
      
      // Stock status
      (filters.stockStatus.length === 0 || (() => {
        const stockStatus = getStockStatus(material).status
        return filters.stockStatus.includes(stockStatus)
      })()) &&
      
      // Price range
      (filters.priceRange.min === null || (material.averageCost || material.lastCostPerUnit || 0) >= filters.priceRange.min) &&
      (filters.priceRange.max === null || (material.averageCost || material.lastCostPerUnit || 0) <= filters.priceRange.max)
    )

    return matchesSearch && matchesCategory && matchesAdvancedFilters
  }

  const applySorting = (materials: Material[]): Material[] => {
    return [...materials].sort((a, b) => {
      let aValue: any
      let bValue: any

      switch (sortConfig.field) {
        case 'name':
          aValue = a.name.toLowerCase()
          bValue = b.name.toLowerCase()
          break
        case 'sku':
          aValue = a.sku?.toLowerCase() || ''
          bValue = b.sku?.toLowerCase() || ''
          break
        case 'category':
          aValue = (a.categoryEntity?.name || a.category || '').toLowerCase()
          bValue = (b.categoryEntity?.name || b.category || '').toLowerCase()
          break
        case 'brand':
          aValue = (a.brandEntity?.name || a.brand || '').toLowerCase()
          bValue = (b.brandEntity?.name || b.brand || '').toLowerCase()
          break
        case 'currentStock':
          aValue = a.currentStock || 0
          bValue = b.currentStock || 0
          break
        case 'averageCost':
          aValue = a.averageCost || a.lastCostPerUnit || 0
          bValue = b.averageCost || b.lastCostPerUnit || 0
          break
        case 'totalValue':
          aValue = (a.currentStock || 0) * (a.averageCost || a.lastCostPerUnit || 0)
          bValue = (b.currentStock || 0) * (b.averageCost || b.lastCostPerUnit || 0)
          break
        case 'reorderPoint':
          aValue = a.reorderPoint || 0
          bValue = b.reorderPoint || 0
          break
        default:
          aValue = a.name.toLowerCase()
          bValue = b.name.toLowerCase()
      }

      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1
      return 0
    })
  }

  // Apply filtering and sorting
  const filteredMaterials = applySorting(materials.filter(applyAdvancedFilters))

  // Helper functions for filter management
  const clearAllFilters = () => {
    setFilters({
      categories: [],
      brands: [],
      colors: [],
      styles: [],
      purities: [],
      specifications: {
        finishes: [],
        diameters: [],
        hardness: [],
        materials: [],
        gauges: []
      },
      stockStatus: [],
      priceRange: { min: null, max: null }
    })
    setSearchTerm('')
    setSelectedCategory('all')
  }

  const handleSort = (field: string) => {
    setSortConfig(prev => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc'
    }))
  }

  const getSortIcon = (field: string) => {
    if (sortConfig.field !== field) return <ArrowUpDown className="w-4 h-4" />
    return sortConfig.direction === 'asc' ? <ArrowUp className="w-4 h-4" /> : <ArrowDown className="w-4 h-4" />
  }

  const fetchMaterials = async () => {
    try {
      console.log('🔍 Fetching materials...')
      const response = await materialsApi.getAll()
      console.log('📦 Materials API response:', response)
      
      // materialsApi.getAll() uses getPaginated which returns PaginatedResponse
      const materialsData = Array.isArray(response) ? response : (response?.data || [])
      console.log('📋 Processed materials data:', materialsData)
      
      setMaterials(materialsData)
    } catch (error: any) {
      console.error('❌ Error fetching materials:', error)
      console.error('❌ Error details:', {
        message: error?.message,
        status: error?.response?.status,
        statusText: error?.response?.statusText,
        data: error?.response?.data
      })
      setMaterials([])
    } finally {
      setIsLoading(false)
    }
  }

  const fetchCategories = async () => {
    try {
      const response = await apiClient.get('/templates/user/form-config')
      const categories = response?.categories || response?.data?.categories || []
      setCategories(categories)
    } catch (error) {
      console.error('Error fetching categories:', error)
      setCategories([])
    }
  }

  const fetchBrands = async () => {
    try {
      // Use template-aware endpoint if template is available
      const endpoint = currentTemplate
        ? `/brands/materials?businessType=${currentTemplate.businessType}`
        : '/brands/materials'
      const response = await apiClient.get(endpoint)
      setBrands(response.data || [])
    } catch (error) {
      console.error('Error fetching brands:', error)
      setBrands([])
    }
  }

  const fetchColors = async () => {
    try {
      // Add timeout and retry logic for colors
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), 30000) // 30 second timeout
      
      // Use template-aware endpoint if template is available
      const endpoint = currentTemplate
        ? `/colors/materials?businessType=${currentTemplate.businessType}`
        : '/colors/materials'
      
      const response = await apiClient.get(endpoint, {
        signal: controller.signal
      })
      
      clearTimeout(timeoutId)
      setColors(response.data || [])
    } catch (error: any) {
      console.error('Error fetching colors:', error)
      
      // Handle timeout gracefully - don't block the page
      if (error.code === 'ECONNABORTED' || error.name === 'AbortError') {
        console.warn('Colors fetch timed out - continuing without colors data')
      }
      
      // Set empty array to prevent UI issues
      setColors([])
    }
  }

  const fetchStyles = async () => {
    try {
      // Use template-aware endpoint if template is available
      const endpoint = currentTemplate
        ? `/styles/materials?businessType=${currentTemplate.businessType}`
        : '/styles/materials'
      const response = await apiClient.get(endpoint)
      setStyles(response.data || [])
    } catch (error) {
      console.error('Error fetching styles:', error)
      setStyles([])
    }
  }

  const fetchPurities = async () => {
    try {
      // Add timeout and retry logic for purities
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), 30000) // 30 second timeout
      
      // Use template-aware endpoint if template is available
      const endpoint = currentTemplate
        ? `/purities/materials?businessType=${currentTemplate.businessType}`
        : '/purities/materials'
      
      const response = await apiClient.get(endpoint, {
        signal: controller.signal
      })
      
      clearTimeout(timeoutId)
      setPurities(response.data || [])
    } catch (error: any) {
      console.error('Error fetching purities:', error)
      
      // Handle timeout gracefully - don't block the page
      if (error.code === 'ECONNABORTED' || error.name === 'AbortError') {
        console.warn('Purities fetch timed out - continuing without purities data')
      }
      
      // Set empty array to prevent UI issues
      setPurities([])
    }
  }

  const fetchPurchaseHistory = async (materialId: string) => {
    if (purchaseHistory[materialId] || loadingPurchases[materialId]) {
      return // Already loaded or loading
    }

    console.log(`🔍 Fetching purchase history for material: ${materialId}`)
    setLoadingPurchases(prev => ({ ...prev, [materialId]: true }))
    
    try {
      const purchases = await materialsApi.getPurchaseHistory(materialId)
      console.log(`📦 Purchase history response for ${materialId}:`, purchases)
      
      // Handle both direct array and response.data structure
      const purchaseArray = Array.isArray(purchases) ? purchases : (purchases?.data || [])
      
      // Sort by purchase date (FIFO - oldest first)
      const sortedPurchases = purchaseArray.sort((a: Purchase, b: Purchase) =>
        new Date(a.purchaseDate).getTime() - new Date(b.purchaseDate).getTime()
      )
      
      console.log(`✅ Sorted purchase history for ${materialId}:`, sortedPurchases)
      
      // Debug: Calculate total from purchases and compare with current stock
      const totalFromPurchases = sortedPurchases.reduce((sum: number, p: Purchase) => {
        const quantity = Number(p.quantity) || 0
        return sum + quantity
      }, 0)
      
      const currentMaterial = materials.find(m => m.id === materialId)
      const currentStock = currentMaterial?.currentStock || 0
      
      console.log(`🔍 STOCK ANALYSIS for ${materialId}:`)
      console.log(`   📦 Current Stock: ${currentStock}`)
      console.log(`   📋 Total from Purchases: ${totalFromPurchases}`)
      console.log(`   ⚖️  Difference: ${currentStock - totalFromPurchases}`)
      
      if (Math.abs(currentStock - totalFromPurchases) > 0.01) {
        console.warn(`⚠️  STOCK DISCREPANCY DETECTED!`)
        console.warn(`   This could indicate:`)
        console.warn(`   - Missing purchase records`)
        console.warn(`   - Stock adjustments not in purchase history`)
        console.warn(`   - Data synchronization issues`)
        
        // Try to fetch stock adjustments to explain the discrepancy
        console.log(`🔍 Fetching stock adjustments to explain discrepancy...`)
        try {
          const adjustments = await materialsApi.getStockAdjustments(materialId)
          console.log(`📋 Stock adjustments for ${materialId}:`, adjustments)
          
          if (adjustments && adjustments.length > 0) {
            const totalAdjustments = adjustments.reduce((sum: number, adj: any) => {
              const quantity = Number(adj.actualQuantityChange) || Number(adj.quantity) || 0
              return sum + quantity
            }, 0)
            
            console.log(`📊 STOCK RECONCILIATION:`)
            console.log(`   📦 Current Stock: ${currentStock}`)
            console.log(`   📋 Total from Purchases: ${totalFromPurchases}`)
            console.log(`   ⚖️  Total from Adjustments: ${totalAdjustments}`)
            console.log(`   🧮 Calculated Total: ${totalFromPurchases + totalAdjustments}`)
            console.log(`   ✅ Difference Explained: ${Math.abs((totalFromPurchases + totalAdjustments) - currentStock) < 0.01 ? 'YES' : 'NO'}`)
          } else {
            console.log(`📝 No stock adjustments found - discrepancy unexplained`)
          }
        } catch (adjustmentError) {
          console.log(`❌ Could not fetch stock adjustments:`, adjustmentError)
        }
      }
      
      setPurchaseHistory(prev => ({ ...prev, [materialId]: sortedPurchases }))
    } catch (error: any) {
      console.error(`❌ Error fetching purchase history for ${materialId}:`, error)
      // Check if it's a 404 (no purchases) vs other errors
      if (error?.response?.status === 404) {
        console.log(`📝 No purchase history found for material ${materialId}`)
        setPurchaseHistory(prev => ({ ...prev, [materialId]: [] }))
      } else {
        console.error(`🚨 API Error:`, error?.response?.data || error?.message)
        setPurchaseHistory(prev => ({ ...prev, [materialId]: [] }))
      }
    } finally {
      setLoadingPurchases(prev => ({ ...prev, [materialId]: false }))
    }
  }

  const togglePurchaseHistory = async (materialId: string) => {
    const isCurrentlyShown = showPurchaseHistory[materialId]
    
    if (!isCurrentlyShown) {
      // Show and fetch if needed
      await fetchPurchaseHistory(materialId)
    }
    
    setShowPurchaseHistory(prev => ({ ...prev, [materialId]: !isCurrentlyShown }))
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 4
    }).format(amount)
  }

  const handleMaterialSelection = (materialId: string) => {
    const newSelected = new Set(selectedMaterials)
    if (newSelected.has(materialId)) {
      newSelected.delete(materialId)
    } else {
      newSelected.add(materialId)
    }
    setSelectedMaterials(newSelected)
  }

  const handleSellMaterial = async (materialId: string, quantity: number, price: number) => {
    try {
      // API call would go here
      console.log('Selling material:', { materialId, quantity, price })
      
      // Reset modal state
      setShowSellModal(null)
      setSellQuantity(1)
      setSellPrice('')
      
      // Refresh materials list
      // fetchMaterials()
    } catch (error) {
      console.error('Error selling material:', error)
    }
  }

  const handleBulkSale = async () => {
    try {
      // Collect sale data from form inputs
      const salesData = Array.from(selectedMaterials).map(materialId => {
        // In real implementation, get values from form inputs
        return {
          materialId,
          quantity: 1,
          price: 0
        }
      })
      
      console.log('Bulk sale:', salesData)
      
      // Reset state
      setShowBulkSale(false)
      setSelectedMaterials(new Set())
      
      // Refresh materials list
      // fetchMaterials()
    } catch (error) {
      console.error('Error processing bulk sale:', error)
    }
  }

  const handleDeleteMaterial = async (materialId: string, materialName: string) => {
    if (!confirm(`Are you sure you want to delete "${materialName}"? This action cannot be undone.`)) {
      return
    }

    try {
      await materialsApi.delete(materialId)
      
      // Remove from local state
      setMaterials(prev => prev.filter(m => m.id !== materialId))
      
      // Remove from selected materials if it was selected
      setSelectedMaterials(prev => {
        const newSelected = new Set(prev)
        newSelected.delete(materialId)
        return newSelected
      })
      
      console.log(`Material "${materialName}" deleted successfully`)
    } catch (error) {
      console.error('Error deleting material:', error)
      alert('Failed to delete material. Please try again.')
    }
  }

  const getStockStatus = (material: Material) => {
    if (material.currentStock <= 0) return { status: 'out-of-stock', label: 'Out of Stock', variant: 'destructive' as const }
    const reorderPoint = material.reorderPoint || 0
    if (material.currentStock <= reorderPoint) return { status: 'low-stock', label: 'Low Stock', variant: 'warning' as const }
    return { status: 'in-stock', label: 'In Stock', variant: 'success' as const }
  }

  const getStockDisplayUnit = (material: Material) => {
    // Use template unit type abbreviation if available
    if (material.templateUnitType?.abbreviation) {
      return material.templateUnitType.abbreviation
    }
    
    // For package-based units, show individual items instead of packages
    const packageUnits = ['package', 'set', 'dozen', 'gross', 'strand', 'pair']
    const unitName = material.templateUnitType?.name || 'units'
    if (packageUnits.includes(unitName.toLowerCase())) {
      return 'pieces'
    }
    return unitName || 'units'
  }

  const getMaterialImage = (material: Material) => {
    // Try imageUrl first, then first item in imageUrls array
    const imageUrl = material.imageUrl || (material.imageUrls && material.imageUrls[0])
    return imageUrl || '/images/no-photo-placeholder.svg'
  }

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    e.currentTarget.src = '/images/no-photo-placeholder.svg'
  }

  // Get template-aware page description
  const getPageDescription = () => {
    if (!currentTemplate) return "Manage your materials and supplies"
    
    switch (currentTemplate.businessType) {
      case 'jewelry':
        return "Manage your jewelry making materials and supplies"
      case '3d_printing':
        return "Manage your 3D printing materials and filaments"
      case 'woodworking':
        return "Manage your woodworking materials and lumber"
      case 'photography':
        return "Manage your photography equipment and supplies"
      case 'automotive':
        return "Manage your automotive parts and supplies"
      case 'electronics':
        return "Manage your electronic components and supplies"
      case 'culinary':
        return "Manage your culinary ingredients and supplies"
      default:
        return `Manage your ${currentTemplate.industry || currentTemplate.name} materials and supplies`
    }
  }

  return (
    <>
      <Helmet>
        <title>Materials - Jewelry Inventory Tracker</title>
      </Helmet>

      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              Materials
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-2">
              {getPageDescription()}
            </p>
            {selectedMaterials.size > 0 && (
              <p className="text-sm text-jewelry-primary mt-1">
                {selectedMaterials.size} material{selectedMaterials.size !== 1 ? 's' : ''} selected
              </p>
            )}
          </div>
          <div className="flex gap-2">
            {selectedMaterials.size > 0 && (
              <Button
                variant="outline"
                onClick={() => setShowBulkSale(true)}
                leftIcon={<ShoppingCart className="w-4 h-4" />}
              >
                Bulk Sale ({selectedMaterials.size})
              </Button>
            )}
            <Link to="/materials/stock-adjustment">
              <Button
                variant="outline"
                leftIcon={<Settings className="w-4 h-4" />}
              >
                Stock Adjustment
              </Button>
            </Link>
            <Link to="/materials/add">
              <Button leftIcon={<Plus className="w-4 h-4" />}>
                Add Material
              </Button>
            </Link>
          </div>
        </div>

        {/* Advanced Filters and Search */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
          <div className="p-6">
            <div className="flex flex-col lg:flex-row gap-4 mb-4">
              {/* Search */}
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Search materials, SKU, brand, color, style, specifications..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 focus:ring-2 focus:ring-jewelry-primary focus:border-transparent"
                  />
                </div>
              </div>

              {/* Quick Category Filter */}
              <div className="w-full lg:w-48">
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-jewelry-primary focus:border-transparent"
                >
                  <option value="all">All Categories</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>


              {/* Advanced Filters Toggle */}
              <Button
                variant="outline"
                onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                leftIcon={<SlidersHorizontal className="w-4 h-4" />}
              >
                Advanced Filters
                <ChevronDown className={`w-4 h-4 ml-2 transition-transform ${showAdvancedFilters ? 'rotate-180' : ''}`} />
              </Button>

              {/* Clear Filters */}
              {(showAdvancedFilters && (
                filters.categories.length > 0 || filters.brands.length > 0 || filters.colors.length > 0 ||
                filters.styles.length > 0 || filters.purities.length > 0 ||
                Object.values(filters.specifications).some(arr => arr.length > 0) ||
                filters.stockStatus.length > 0 || filters.priceRange.min !== null || filters.priceRange.max !== null
              )) && (
                <Button
                  variant="outline"
                  onClick={clearAllFilters}
                  leftIcon={<X className="w-4 h-4" />}
                >
                  Clear All
                </Button>
              )}

              {/* View Mode Toggle */}
              <div className="flex rounded-lg border border-gray-300 dark:border-gray-600 overflow-hidden">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`px-3 py-2 ${
                    viewMode === 'grid'
                      ? 'bg-jewelry-primary text-white'
                      : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                  }`}
                >
                  <Grid3X3 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`px-3 py-2 ${
                    viewMode === 'list'
                      ? 'bg-jewelry-primary text-white'
                      : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                  }`}
                >
                  <List className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Advanced Filters Panel */}
            {showAdvancedFilters && (
              <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  
                  {/* Brands Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Brands ({brands.length})
                    </label>
                    <div className="max-h-32 overflow-y-auto border border-gray-300 dark:border-gray-600 rounded-lg p-2 bg-gray-50 dark:bg-gray-700">
                      {brands.map((brand) => (
                        <label key={brand.id} className="flex items-center space-x-2 py-1 hover:bg-gray-100 dark:hover:bg-gray-600 rounded px-1">
                          <input
                            type="checkbox"
                            checked={filters.brands.includes(brand.id)}
                            onChange={(e) => {
                              const newBrands = e.target.checked
                                ? [...filters.brands, brand.id]
                                : filters.brands.filter(id => id !== brand.id)
                              setFilters(prev => ({ ...prev, brands: newBrands }))
                            }}
                            className="rounded border-gray-300 text-jewelry-primary focus:ring-jewelry-primary"
                          />
                          <span className="text-sm text-gray-700 dark:text-gray-300">{brand.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Colors Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Colors ({colors.length})
                    </label>
                    <div className="max-h-32 overflow-y-auto border border-gray-300 dark:border-gray-600 rounded-lg p-2 bg-gray-50 dark:bg-gray-700">
                      {colors.map((color) => (
                        <label key={color.id} className="flex items-center space-x-2 py-1 hover:bg-gray-100 dark:hover:bg-gray-600 rounded px-1">
                          <input
                            type="checkbox"
                            checked={filters.colors.includes(color.id)}
                            onChange={(e) => {
                              const newColors = e.target.checked
                                ? [...filters.colors, color.id]
                                : filters.colors.filter(id => id !== color.id)
                              setFilters(prev => ({ ...prev, colors: newColors }))
                            }}
                            className="rounded border-gray-300 text-jewelry-primary focus:ring-jewelry-primary"
                          />
                          <div className="flex items-center space-x-2">
                            {color.hexCode && (
                              <div
                                className="w-3 h-3 rounded-full border border-gray-300"
                                style={{ backgroundColor: color.hexCode }}
                              />
                            )}
                            <span className="text-sm text-gray-700 dark:text-gray-300">{color.name}</span>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Finishes Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Finishes ({getUniqueSpecificationValues('finish').length})
                    </label>
                    <div className="max-h-32 overflow-y-auto border border-gray-300 dark:border-gray-600 rounded-lg p-2 bg-gray-50 dark:bg-gray-700">
                      {getUniqueSpecificationValues('finish').map((finish) => (
                        <label key={finish} className="flex items-center space-x-2 py-1 hover:bg-gray-100 dark:hover:bg-gray-600 rounded px-1">
                          <input
                            type="checkbox"
                            checked={filters.specifications.finishes.includes(finish)}
                            onChange={(e) => {
                              const newFinishes = e.target.checked
                                ? [...filters.specifications.finishes, finish]
                                : filters.specifications.finishes.filter(f => f !== finish)
                              setFilters(prev => ({
                                ...prev,
                                specifications: { ...prev.specifications, finishes: newFinishes }
                              }))
                            }}
                            className="rounded border-gray-300 text-jewelry-primary focus:ring-jewelry-primary"
                          />
                          <span className="text-sm text-gray-700 dark:text-gray-300">{finish}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Materials Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Materials ({getUniqueSpecificationValues('material').length})
                    </label>
                    <div className="max-h-32 overflow-y-auto border border-gray-300 dark:border-gray-600 rounded-lg p-2 bg-gray-50 dark:bg-gray-700">
                      {getUniqueSpecificationValues('material').map((material) => (
                        <label key={material} className="flex items-center space-x-2 py-1 hover:bg-gray-100 dark:hover:bg-gray-600 rounded px-1">
                          <input
                            type="checkbox"
                            checked={filters.specifications.materials.includes(material)}
                            onChange={(e) => {
                              const newMaterials = e.target.checked
                                ? [...filters.specifications.materials, material]
                                : filters.specifications.materials.filter(m => m !== material)
                              setFilters(prev => ({
                                ...prev,
                                specifications: { ...prev.specifications, materials: newMaterials }
                              }))
                            }}
                            className="rounded border-gray-300 text-jewelry-primary focus:ring-jewelry-primary"
                          />
                          <span className="text-sm text-gray-700 dark:text-gray-300">{material}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Gauges Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Gauges ({getUniqueSpecificationValues('gauge').length})
                    </label>
                    <div className="max-h-32 overflow-y-auto border border-gray-300 dark:border-gray-600 rounded-lg p-2 bg-gray-50 dark:bg-gray-700">
                      {getUniqueSpecificationValues('gauge').map((gauge) => (
                        <label key={gauge} className="flex items-center space-x-2 py-1 hover:bg-gray-100 dark:hover:bg-gray-600 rounded px-1">
                          <input
                            type="checkbox"
                            checked={filters.specifications.gauges.includes(gauge)}
                            onChange={(e) => {
                              const newGauges = e.target.checked
                                ? [...filters.specifications.gauges, gauge]
                                : filters.specifications.gauges.filter(g => g !== gauge)
                              setFilters(prev => ({
                                ...prev,
                                specifications: { ...prev.specifications, gauges: newGauges }
                              }))
                            }}
                            className="rounded border-gray-300 text-jewelry-primary focus:ring-jewelry-primary"
                          />
                          <span className="text-sm text-gray-700 dark:text-gray-300">{gauge}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Hardness Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Hardness ({getUniqueSpecificationValues('hardness').length})
                    </label>
                    <div className="max-h-32 overflow-y-auto border border-gray-300 dark:border-gray-600 rounded-lg p-2 bg-gray-50 dark:bg-gray-700">
                      {getUniqueSpecificationValues('hardness').map((hardness) => (
                        <label key={hardness} className="flex items-center space-x-2 py-1 hover:bg-gray-100 dark:hover:bg-gray-600 rounded px-1">
                          <input
                            type="checkbox"
                            checked={filters.specifications.hardness.includes(hardness)}
                            onChange={(e) => {
                              const newHardness = e.target.checked
                                ? [...filters.specifications.hardness, hardness]
                                : filters.specifications.hardness.filter(h => h !== hardness)
                              setFilters(prev => ({
                                ...prev,
                                specifications: { ...prev.specifications, hardness: newHardness }
                              }))
                            }}
                            className="rounded border-gray-300 text-jewelry-primary focus:ring-jewelry-primary"
                          />
                          <span className="text-sm text-gray-700 dark:text-gray-300">{hardness}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Stock Status Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Stock Status
                    </label>
                    <div className="space-y-2">
                      {[
                        { value: 'in-stock', label: 'In Stock', color: 'text-green-600' },
                        { value: 'low-stock', label: 'Low Stock', color: 'text-yellow-600' },
                        { value: 'out-of-stock', label: 'Out of Stock', color: 'text-red-600' }
                      ].map((status) => (
                        <label key={status.value} className="flex items-center space-x-2 hover:bg-gray-100 dark:hover:bg-gray-600 rounded px-1 py-1">
                          <input
                            type="checkbox"
                            checked={filters.stockStatus.includes(status.value)}
                            onChange={(e) => {
                              const newStatus = e.target.checked
                                ? [...filters.stockStatus, status.value]
                                : filters.stockStatus.filter(s => s !== status.value)
                              setFilters(prev => ({ ...prev, stockStatus: newStatus }))
                            }}
                            className="rounded border-gray-300 text-jewelry-primary focus:ring-jewelry-primary"
                          />
                          <span className={`text-sm font-medium ${status.color}`}>{status.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Price Range Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Price Range ($)
                    </label>
                    <div className="space-y-2">
                      <input
                        type="number"
                        placeholder="Min price"
                        value={filters.priceRange.min || ''}
                        onChange={(e) => setFilters(prev => ({
                          ...prev,
                          priceRange: { ...prev.priceRange, min: e.target.value ? parseFloat(e.target.value) : null }
                        }))}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-jewelry-primary focus:border-transparent"
                      />
                      <input
                        type="number"
                        placeholder="Max price"
                        value={filters.priceRange.max || ''}
                        onChange={(e) => setFilters(prev => ({
                          ...prev,
                          priceRange: { ...prev.priceRange, max: e.target.value ? parseFloat(e.target.value) : null }
                        }))}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-jewelry-primary focus:border-transparent"
                      />
                    </div>
                  </div>

                </div>

                {/* Results Summary */}
                <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      Showing <span className="font-medium text-jewelry-primary">{filteredMaterials.length}</span> of <span className="font-medium">{materials.length}</span> materials
                    </span>
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-gray-600 dark:text-gray-400">Sort by:</span>
                      <select
                        value={sortConfig.field}
                        onChange={(e) => handleSort(e.target.value)}
                        className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-jewelry-primary focus:border-transparent"
                      >
                        <option value="name">Name</option>
                        <option value="sku">SKU</option>
                        <option value="category">Category</option>
                        <option value="brand">Brand</option>
                        <option value="currentStock">Stock</option>
                        <option value="averageCost">Price</option>
                        <option value="totalValue">Total Value</option>
                      </select>
                      <button
                        onClick={() => handleSort(sortConfig.field)}
                        className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                        title={`Sort ${sortConfig.direction === 'asc' ? 'descending' : 'ascending'}`}
                      >
                        {getSortIcon(sortConfig.field)}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Materials Content */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
          {filteredMaterials.length === 0 ? (
            <div className="text-center py-12">
              <Package className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                No materials found
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                Get started by adding your first material to the inventory.
              </p>
              <Link to="/materials/add">
                <Button leftIcon={<Plus className="w-4 h-4" />}>
                  Add Your First Material
                </Button>
              </Link>
            </div>
          ) : (
            <div className="p-6">
              {viewMode === 'grid' ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {filteredMaterials.map((material) => {
                    const stockStatus = getStockStatus(material)
                    const isSelected = selectedMaterials.has(material.id)
                    
                    return (
                      <div
                        key={material.id}
                        className={`border rounded-lg overflow-hidden hover:shadow-md transition-all ${
                          isSelected
                            ? 'border-jewelry-primary bg-jewelry-primary/5'
                            : 'border-gray-200 dark:border-gray-700'
                        }`}
                      >
                        {/* Material Image */}
                        <div className="relative h-48 bg-gray-100 dark:bg-gray-700">
                          <img
                            src={getMaterialImage(material)}
                            alt={material.name}
                            onError={handleImageError}
                            className="w-full h-full object-cover"
                          />
                          <div className="absolute top-2 right-2">
                            <Badge variant={stockStatus.variant}>
                              {stockStatus.label}
                            </Badge>
                          </div>
                          <div className="absolute top-2 left-2">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => handleMaterialSelection(material.id)}
                              className="rounded border-gray-300 text-jewelry-primary focus:ring-jewelry-primary bg-white"
                            />
                          </div>
                        </div>

                        <div className="p-4">
                          <div className="mb-3">
                            <h3 className="font-medium text-gray-900 dark:text-white mb-1">
                              {material.name}
                            </h3>
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                              SKU: {material.sku}
                            </p>
                          </div>
                        
                        <div className="space-y-2 mb-4">
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600 dark:text-gray-400">Stock:</span>
                            <span className="text-gray-900 dark:text-white">
                              {Math.floor(material.currentStock || 0)} {getStockDisplayUnit(material)}
                            </span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600 dark:text-gray-400">Avg Cost:</span>
                            <span className="text-gray-900 dark:text-white">
                              ${Number(material.averageCost || material.lastCostPerUnit || 0).toFixed(2)}
                            </span>
                          </div>
                          {material.specifications?.color && (
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-600 dark:text-gray-400">Color:</span>
                              <span className="text-gray-900 dark:text-white">
                                {material.specifications.color}
                              </span>
                            </div>
                          )}
                          {material.specifications?.gauge && (
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-600 dark:text-gray-400">Gauge:</span>
                              <span className="text-gray-900 dark:text-white">
                                {material.specifications.gauge}
                              </span>
                            </div>
                          )}
                        </div>

                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setShowSellModal(material.id)}
                            disabled={stockStatus.status === 'out-of-stock'}
                            className="flex-1"
                          >
                            <DollarSign className="w-3 h-3 mr-1" />
                            Sell
                          </Button>
                          <Link to={`/inventory/purchase?materialId=${material.id}`}>
                            <Button variant="outline" size="sm" title="Repurchase Material">
                              <ShoppingCart className="w-3 h-3" />
                            </Button>
                          </Link>
                          <Link to={`/materials/edit/${material.id}`}>
                            <Button variant="outline" size="sm" title="Edit Material">
                              <Edit className="w-3 h-3" />
                            </Button>
                          </Link>
                          <Button variant="outline" size="sm" title="View Details">
                            <Eye className="w-3 h-3" />
                          </Button>
                        </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
                        <th className="text-left py-3 px-4 font-medium text-gray-900 dark:text-white w-12">
                          <input
                            type="checkbox"
                            checked={filteredMaterials.length > 0 && selectedMaterials.size === filteredMaterials.length}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedMaterials(new Set(filteredMaterials.map(m => m.id)))
                              } else {
                                setSelectedMaterials(new Set())
                              }
                            }}
                            className="rounded border-gray-300 text-jewelry-primary focus:ring-jewelry-primary"
                          />
                        </th>
                        <th className="text-left py-3 px-4 font-medium text-gray-900 dark:text-white w-16">
                          Image
                        </th>
                        <th className="text-left py-3 px-4 font-medium text-gray-900 dark:text-white min-w-[200px]">
                          Material
                        </th>
                        <th className="text-left py-3 px-4 font-medium text-gray-900 dark:text-white">
                          Category
                        </th>
                        <th className="text-left py-3 px-4 font-medium text-gray-900 dark:text-white">
                          Brand
                        </th>
                        <th className="text-right py-3 px-4 font-medium text-gray-900 dark:text-white">
                          Current Stock
                        </th>
                        <th className="text-right py-3 px-4 font-medium text-gray-900 dark:text-white">
                          Reorder Point
                        </th>
                        <th className="text-right py-3 px-4 font-medium text-gray-900 dark:text-white">
                          Avg Cost
                        </th>
                        <th className="text-right py-3 px-4 font-medium text-gray-900 dark:text-white">
                          Total Value
                        </th>
                        <th className="text-left py-3 px-4 font-medium text-gray-900 dark:text-white">
                          Specifications
                        </th>
                        <th className="text-center py-3 px-4 font-medium text-gray-900 dark:text-white">
                          Status
                        </th>
                        <th className="text-right py-3 px-4 font-medium text-gray-900 dark:text-white w-32">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredMaterials.map((material) => {
                        const stockStatus = getStockStatus(material)
                        const isSelected = selectedMaterials.has(material.id)
                        const currentStock = material.currentStock || 0
                        const avgCost = Number(material.averageCost || material.lastCostPerUnit || 0)
                        const totalValue = currentStock * avgCost
                        
                        return (
                          <React.Fragment key={material.id}>
                            <tr
                              className={`border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 ${
                                isSelected ? 'bg-jewelry-primary/5' : ''
                              }`}
                            >
                              <td className="py-3 px-4">
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => handleMaterialSelection(material.id)}
                                  className="rounded border-gray-300 text-jewelry-primary focus:ring-jewelry-primary"
                                />
                              </td>
                              <td className="py-3 px-4">
                                <div className="flex items-center gap-2">
                                  <div className="w-12 h-12 rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-700">
                                    <img
                                      src={getMaterialImage(material)}
                                      alt={material.name}
                                      onError={handleImageError}
                                      className="w-full h-full object-cover"
                                    />
                                  </div>
                                  {/* FIFO Purchase History Toggle */}
                                  <button
                                    onClick={() => togglePurchaseHistory(material.id)}
                                    className="p-1 text-gray-400 hover:text-jewelry-primary transition-colors"
                                    title="View FIFO Purchase History"
                                    disabled={loadingPurchases[material.id]}
                                  >
                                    {loadingPurchases[material.id] ? (
                                      <div className="w-4 h-4 border-2 border-gray-300 border-t-jewelry-primary rounded-full animate-spin" />
                                    ) : showPurchaseHistory[material.id] ? (
                                      <ChevronDown className="w-4 h-4" />
                                    ) : (
                                      <ChevronRight className="w-4 h-4" />
                                    )}
                                  </button>
                                </div>
                              </td>
                              <td className="py-3 px-4">
                                <div>
                                  <div className="font-medium text-gray-900 dark:text-white">
                                    {material.name}
                                  </div>
                                  <div className="text-sm text-gray-600 dark:text-gray-400">
                                    SKU: {material.sku || 'N/A'}
                                  </div>
                                </div>
                              </td>
                              <td className="py-3 px-4">
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                                  {(() => {
                                    // First try to use the category relation from backend
                                    if (material.categoryEntity?.name) {
                                      return material.categoryEntity.name
                                    }
                                    
                                    // Then try to find category by ID in the categories list
                                    const categoryById = categories.find(cat => cat.id === material.categoryId)
                                    if (categoryById) return categoryById.name
                                    
                                    // Fall back to category name if it exists
                                    const categoryByName = categories.find(cat => cat.name === material.category)
                                    if (categoryByName) return categoryByName.name
                                    
                                    // Final fallback
                                    return material.category || 'Other'
                                  })()}
                                </span>
                              </td>
                              <td className="py-3 px-4 text-gray-900 dark:text-white">
                                {(() => {
                                  // First try to use the brand relation from backend
                                  if (material.brandEntity?.name) {
                                    return material.brandEntity.name
                                  }
                                  
                                  // Fall back to legacy brand field
                                  return material.brand || '-'
                                })()}
                              </td>
                              <td className="py-3 px-4 text-right">
                                <div className="font-medium text-gray-900 dark:text-white">
                                  {Math.floor(currentStock).toLocaleString()}
                                </div>
                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                  {getStockDisplayUnit(material)}
                                </div>
                              </td>
                              <td className="py-3 px-4 text-right text-gray-900 dark:text-white">
                                {Math.floor(material.reorderPoint || 0).toLocaleString()}
                              </td>
                              <td className="py-3 px-4 text-right text-gray-900 dark:text-white">
                                ${avgCost.toFixed(2)}
                              </td>
                              <td className="py-3 px-4 text-right">
                                <div className="font-medium text-gray-900 dark:text-white">
                                  ${totalValue.toFixed(2)}
                                </div>
                              </td>
                              <td className="py-3 px-4">
                                <div className="text-sm text-gray-600 dark:text-gray-400 max-w-[150px]">
                                  {material.specifications?.color && (
                                    <div>Color: {material.specifications.color}</div>
                                  )}
                                  {material.specifications?.gauge && (
                                    <div>Gauge: {material.specifications.gauge}</div>
                                  )}
                                  {material.specifications?.material && (
                                    <div>Material: {material.specifications.material}</div>
                                  )}
                                  {material.specifications?.finish && (
                                    <div>Finish: {material.specifications.finish}</div>
                                  )}
                                  {!material.specifications?.color && !material.specifications?.gauge &&
                                   !material.specifications?.material && !material.specifications?.finish && '-'}
                                </div>
                              </td>
                              <td className="py-3 px-4 text-center">
                                <Badge variant={stockStatus.variant}>
                                  {stockStatus.label}
                                </Badge>
                              </td>
                              <td className="py-3 px-4">
                                <div className="flex justify-end gap-1">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setShowSellModal(material.id)}
                                    disabled={stockStatus.status === 'out-of-stock'}
                                    title="Sell Material"
                                  >
                                    <DollarSign className="w-3 h-3" />
                                  </Button>
                                  <Link to={`/inventory/purchase?materialId=${material.id}`}>
                                    <Button variant="outline" size="sm" title="Repurchase Material">
                                      <ShoppingCart className="w-3 h-3" />
                                    </Button>
                                  </Link>
                                  <Link to={`/materials/edit/${material.id}`}>
                                    <Button variant="outline" size="sm" title="Edit Material">
                                      <Edit className="w-3 h-3" />
                                    </Button>
                                  </Link>
                                  <Button variant="outline" size="sm" title="View Details">
                                    <Eye className="w-3 h-3" />
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    title="Delete Material"
                                    onClick={() => handleDeleteMaterial(material.id, material.name)}
                                    className="text-red-600 border-red-300 hover:bg-red-50 dark:text-red-400 dark:border-red-600 dark:hover:bg-red-900/20"
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                            {/* FIFO Purchase History Dropdown */}
                            {showPurchaseHistory[material.id] && (
                              <tr>
                                <td colSpan={12} className="py-0 px-4 bg-gray-50 dark:bg-gray-700/30">
                                  <div className="py-4 border-l-4 border-jewelry-primary">
                                    <div className="pl-4">
                                      <div className="flex items-center gap-2 mb-3">
                                        <Receipt className="w-4 h-4 text-jewelry-primary" />
                                        <h4 className="font-medium text-gray-900 dark:text-white">
                                          FIFO Purchase History
                                        </h4>
                                        <span className="text-xs text-gray-500 dark:text-gray-400">
                                          (Oldest First - FIFO Order)
                                        </span>
                                      </div>
                                      
                                      {purchaseHistory[material.id]?.length > 0 ? (
                                        <div className="space-y-2">
                                          {purchaseHistory[material.id].map((purchase, index) => (
                                            <div
                                              key={purchase.id}
                                              className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600"
                                            >
                                              <div className="flex items-center gap-4">
                                                <div className="flex items-center gap-2">
                                                  <span className="inline-flex items-center justify-center w-6 h-6 bg-jewelry-primary text-white text-xs font-medium rounded-full">
                                                    {index + 1}
                                                  </span>
                                                  <Calendar className="w-4 h-4 text-gray-400" />
                                                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                                                    {formatDate(purchase.purchaseDate)}
                                                  </span>
                                                </div>
                                                
                                                {purchase.supplier && (
                                                  <div className="text-sm text-gray-600 dark:text-gray-400">
                                                    Supplier: {purchase.supplier}
                                                  </div>
                                                )}
                                                
                                                {purchase.invoiceNumber && (
                                                  <div className="text-sm text-gray-600 dark:text-gray-400">
                                                    Invoice: {purchase.invoiceNumber}
                                                  </div>
                                                )}
                                              </div>
                                              
                                              <div className="flex items-center gap-6 text-sm">
                                                <div className="text-right">
                                                  <div className="font-medium text-gray-900 dark:text-white">
                                                    {Math.floor(purchase.quantity).toLocaleString()} {getStockDisplayUnit(material)}
                                                  </div>
                                                  <div className="text-xs text-gray-500 dark:text-gray-400">
                                                    Quantity
                                                  </div>
                                                </div>
                                                
                                                <div className="text-right">
                                                  <div className="font-medium text-gray-900 dark:text-white">
                                                    {formatCurrency(purchase.unitCost)}
                                                  </div>
                                                  <div className="text-xs text-gray-500 dark:text-gray-400">
                                                    Cost/Unit
                                                  </div>
                                                </div>
                                                
                                                <div className="text-right">
                                                  <div className="font-medium text-jewelry-primary">
                                                    {formatCurrency(purchase.totalCost)}
                                                  </div>
                                                  <div className="text-xs text-gray-500 dark:text-gray-400">
                                                    Total Cost
                                                  </div>
                                                </div>
                                                
                                                {purchase.packageQuantity && purchase.itemsPerPackage && (
                                                  <div className="text-right">
                                                    <div className="font-medium text-gray-900 dark:text-white">
                                                      {purchase.packageQuantity} × {purchase.itemsPerPackage}
                                                    </div>
                                                    <div className="text-xs text-gray-500 dark:text-gray-400">
                                                      Packages × Items
                                                    </div>
                                                  </div>
                                                )}
                                              </div>
                                            </div>
                                          ))}
                                          
                                          {/* Summary */}
                                          <div className="mt-4 p-3 bg-jewelry-primary/10 rounded-lg border border-jewelry-primary/20">
                                            <div className="flex items-center justify-between">
                                              <span className="font-medium text-gray-900 dark:text-white">
                                                Total Purchases: {purchaseHistory[material.id].length}
                                              </span>
                                              <div className="flex items-center gap-4 text-sm">
                                                <span className="font-medium text-gray-900 dark:text-white">
                                                  Total Quantity: {Math.floor(purchaseHistory[material.id].reduce((sum, p) => {
                                                    const quantity = Number(p.quantity) || 0
                                                    return sum + quantity
                                                  }, 0)).toLocaleString()} {getStockDisplayUnit(material)}
                                                </span>
                                                <span className="font-medium text-jewelry-primary">
                                                  Total Value: {formatCurrency(purchaseHistory[material.id].reduce((sum, p) => {
                                                    const totalCost = Number(p.totalCost) || 0
                                                    return sum + totalCost
                                                  }, 0))}
                                                </span>
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      ) : (
                                        <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                                          No purchase history found for this material.
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Sell Material Modal */}
      {showSellModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Sell Material</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Quantity</label>
                <Input
                  type="number"
                  min="1"
                  max={filteredMaterials.find(m => m.id === showSellModal)?.currentStock || materials.find(m => m.id === showSellModal)?.currentStock || 1}
                  value={sellQuantity}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSellQuantity(parseInt(e.target.value) || 1)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Sale Price</label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={sellPrice}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSellPrice(e.target.value)}
                />
              </div>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowSellModal(null)
                    setSellQuantity(1)
                    setSellPrice('')
                  }}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => {
                    if (showSellModal && sellPrice) {
                      handleSellMaterial(showSellModal, sellQuantity, parseFloat(sellPrice))
                    }
                  }}
                  disabled={!sellPrice}
                  className="flex-1"
                >
                  Sell
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Sale Modal */}
      {showBulkSale && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-2xl">
            <h3 className="text-lg font-semibold mb-4">Bulk Material Sale</h3>
            <div className="space-y-4">
              <div className="max-h-60 overflow-y-auto">
                {Array.from(selectedMaterials).map(materialId => {
                  const material = filteredMaterials.find(m => m.id === materialId) || materials.find(m => m.id === materialId)
                  if (!material) return null
                  
                  return (
                    <div key={materialId} className="flex items-center justify-between p-3 border rounded-lg">
                      <div>
                        <h4 className="font-medium">{material.name}</h4>
                        <p className="text-sm text-gray-600">Stock: {material.currentStock || 0} {getStockDisplayUnit(material)}</p>
                      </div>
                      <div className="flex gap-2">
                        <Input
                          type="number"
                          min="1"
                          max={material.currentStock}
                          defaultValue="1"
                          className="w-20"
                        />
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="Price"
                          className="w-24"
                        />
                      </div>
                    </div>
                  )
                })}
              </div>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setShowBulkSale(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleBulkSale}
                  className="flex-1"
                >
                  Process Sales
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}